/**
 * LoR breadcrumb strip rendered above the 3D viewport.
 * RFC-114 Phase 1 — shows the Earth > Region > Site > Asset navigation path,
 * with labels sourced from the dataset's own scope (Block 1.5 / D4), e.g.
 * "Earth > Tokyo > Shinjuku > Building A" for a Tokyo gTwin.
 * Only render this component when engine === "giro3d" (caller-side gate).
 */
import type { CSSProperties } from 'react';
import { tokens } from '@/design';
import type { LoRState } from '@/engines/giro3d/lorController';

export interface LorBreadcrumbProps {
  readonly state: LoRState;
  /** Called when the "Earth" (first) crumb is clicked — returns to LoR0. */
  readonly onHome: () => void;
}

export function LorBreadcrumb({ state, onHome }: LorBreadcrumbProps) {
  return (
    <div style={containerStyle} role="navigation" aria-label="Level of Representation breadcrumb">
      {state.breadcrumb.map((crumb, index) => {
        const isLast = index === state.breadcrumb.length - 1;
        const isFirst = index === 0;
        return (
          <span key={index} style={itemWrapStyle}>
            {index > 0 && <span style={separatorStyle} aria-hidden="true">›</span>}
            <button
              style={isLast ? lastCrumbButtonStyle : crumbButtonStyle}
              aria-label={crumb}
              onClick={isFirst ? onHome : undefined}
              tabIndex={isFirst ? 0 : -1}
            >
              {crumb}
            </button>
          </span>
        );
      })}
    </div>
  );
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: tokens.space[1],
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  borderRadius: tokens.radius.pill,
  background: tokens.color.surface[3],
  color: tokens.color.ink.primary,
  fontSize: 12,
  lineHeight: 1.4,
  height: 28,
  pointerEvents: 'auto',
  userSelect: 'none',
  marginBottom: tokens.space[1],
};

const itemWrapStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.space[1],
};

const separatorStyle: CSSProperties = {
  opacity: 0.5,
  fontSize: 11,
  lineHeight: 1,
};

const crumbButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: tokens.color.ink.primary,
  cursor: 'pointer',
  fontSize: 12,
  padding: 0,
  opacity: 0.75,
  fontFamily: 'inherit',
  lineHeight: 1.4,
};

const lastCrumbButtonStyle: CSSProperties = {
  ...crumbButtonStyle,
  fontWeight: 700,
  opacity: 1,
  color: tokens.color.semantic.info,
  cursor: 'default',
};
